/**
 * useNavSubscription — subscribes the Pages module to the real-time nav channel
 * so new libraries and pages (plus renames/moves/deletes) appear in the
 * left-hand navigation without a manual refresh.
 *
 * Reuses the shared websocket-client (createWebSocketClient) so the nav socket
 * inherits the same reconnect/backoff/session-expiry behaviour as the board and
 * page paths. One nav socket serves the whole module — mount it once in the
 * stable Pages layout, NOT per-library.
 *
 * On-the-wire message contract (LOCKED, matched to the fan-out nav-broadcaster):
 * `{ eventType: '<dotted detail-type>', timestamp, ...payload }` — the raw
 * dotted `eventType` at the top level and payload fields spread to the top
 * level. Switch on `data.eventType`; there is NO `data.detailType` on the wire.
 */

import { useEffect, useRef } from 'react';
import { appConfig } from '../../config';
import { useAuth } from '../../state/auth-context';
import { usePagesContext } from '../../state/pages-context';
import type { PageTreeNode } from '../../state/pages-context';
import type { Library, LibraryVisibility } from '../../api/pages-api';
import { createWebSocketClient, type WebSocketClient } from '../../services/websocket-client';

/** Builds a full Library from a library nav event (payload spread to top level). */
function toLibrary(data: Record<string, unknown>): Library {
  const createdAt = (data.createdAt as string) ?? '';
  return {
    libraryId: data.libraryId as string,
    name: (data.name as string) ?? '',
    // The event omits description/updatedAt; the authoritative values arrive on
    // the next fetchLibraries(). Default them so the object is well-formed.
    description: (data.description as string) ?? '',
    visibility: (data.visibility as LibraryVisibility) ?? 'private',
    createdBy: (data.createdBy as string) ?? '',
    createdAt,
    updatedAt: (data.updatedAt as string) ?? createdAt,
  };
}

/** Builds a full PageTreeNode from a page nav event. children is always []. */
function toPageTreeNode(data: Record<string, unknown>): PageTreeNode {
  return {
    pageId: data.pageId as string,
    title: (data.title as string) ?? '',
    parentPageId: (data.parentPageId as string | null) ?? null,
    sortOrder: (data.sortOrder as string) ?? 'z0',
    // The event never carries children; insertNodeIntoTree dereferences this,
    // so it must always be present.
    children: [],
  };
}

/**
 * Subscribe to the real-time nav channel and dispatch reducer updates.
 * No-ops when no WS URL is configured (backend not yet deployed) — the nav
 * still works via refetch.
 */
export function useNavSubscription(): void {
  const { getAccessToken } = useAuth();
  const { dispatch } = usePagesContext();
  const clientRef = useRef<WebSocketClient | null>(null);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    // Skip entirely if no WS URL is configured (backend not yet deployed).
    if (!appConfig.wsUrl) return;

    let cancelled = false;

    // Resolve a fresh WS URL (with a fresh token) for each connect attempt.
    // The nav socket needs no boardId/pageId — it subscribes to the viewer's
    // own user feed. Returning null tells the client to stop retrying.
    const getUrl = async (): Promise<string | null> => {
      const token = await getAccessToken();
      if (!token) return null;
      return `${appConfig.wsUrl}?nav=1&token=${encodeURIComponent(token)}`;
    };

    const client = createWebSocketClient({
      boardId: 'nav',
      getUrl,
      createSocket: (url: string) => new WebSocket(url),
      onConnect: (send) => {
        // Re-assert the nav subscription on every (re)connect.
        send(JSON.stringify({ action: 'subscribeNav' }));
      },
      onEvent: (data: Record<string, unknown>) => {
        if (cancelled) return;

        const eventType = data.eventType as string | undefined;
        const libraryId = data.libraryId as string | undefined;
        const pageId = data.pageId as string | undefined;

        switch (eventType) {
          case 'library.created':
            dispatchRef.current({ type: 'WS_LIBRARY_CREATED', payload: { library: toLibrary(data) } });
            break;
          case 'library.updated':
            if (libraryId) {
              dispatchRef.current({
                type: 'WS_LIBRARY_UPDATED',
                payload: {
                  libraryId,
                  name: data.name as string | undefined,
                  visibility: data.visibility as LibraryVisibility | undefined,
                },
              });
            }
            break;
          case 'library.deleted':
            if (libraryId) {
              dispatchRef.current({ type: 'WS_LIBRARY_DELETED', payload: { libraryId } });
            }
            break;
          case 'page.created.nav':
            if (libraryId) {
              dispatchRef.current({
                type: 'WS_PAGE_CREATED',
                payload: { libraryId, node: toPageTreeNode(data) },
              });
            }
            break;
          case 'page.updated.nav':
            if (libraryId && pageId) {
              dispatchRef.current({
                type: 'WS_PAGE_UPDATED',
                payload: { libraryId, pageId, title: (data.title as string) ?? '' },
              });
            }
            break;
          case 'page.moved.nav':
            if (libraryId && pageId) {
              dispatchRef.current({
                type: 'WS_PAGE_MOVED',
                payload: {
                  libraryId,
                  pageId,
                  parentPageId: (data.parentPageId as string | null) ?? null,
                  sortOrder: (data.sortOrder as string) ?? 'z0',
                },
              });
            }
            break;
          case 'page.deleted.nav':
            if (libraryId && pageId) {
              dispatchRef.current({ type: 'WS_PAGE_DELETED', payload: { libraryId, pageId } });
            }
            break;
          default:
            // Unknown eventType — ignore.
            break;
        }
      },
      onStateChange: () => {
        // Connection state is not surfaced for the nav; reconnect is internal.
      },
    });

    clientRef.current = client;
    client.connect();

    return () => {
      cancelled = true;
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    };
  }, [getAccessToken]);
}
